import { useContext } from "react";
import useApi from "./useApi";
import { UserAccountContext } from "../contexts/UserAccountContext";

const useUserAccountSettings = () => {
    const { getJson, patchJson } = useApi();
    const { settings, updateSettings } = useContext(UserAccountContext);

    /**
     * Fetches user account company settings
     * @returns {Promise<object>}
     */
    const getUserAccountCompanySettings = async () => {
        // If settings are already loaded, return them
        if (Object.keys(settings).length > 0) {
            return settings;
        }
        const response = await getJson('/online/useraccount/companysettings');
        updateSettings(response);
    }

    /**
     * Updates company settings with trimmed values.
     * @param {object} newSettings - The settings to update
     * @returns {Promise<void>}
     */
    const updateUserAccountCompanySettings = async (newSettings) => {
        const updatedSettings = resolveUpdatedSettings(newSettings);
        if (Object.keys(updatedSettings).length === 0) {
            return; // No changes to update
        }
        await patchJson('/online/useraccount/companysettings', updatedSettings);
        updateSettings(updatedSettings);
    }

    /**
     * Fetches user account settings
     * @returns {Promise<object>}
     */
    const getUserAccountSettings = async () => {
        // If settings are already loaded, return them
        if (Object.keys(settings).length > 0) {
            return settings;
        }
        const response = await getJson('/online/useraccount/settings');
        updateSettings(response);
    }

    /**
     * Updates user account settings with trimmed values.
     * @param {object} newSettings - The settings to update
     * @returns {Promise<void>}
     */
    const updateUserAccountSettings = async (newSettings) => {
        const updatedSettings = resolveUpdatedSettings(newSettings);
        if (Object.keys(updatedSettings).length === 0) {
            return; // No changes to update
        }
        await patchJson('/online/useraccount/settings', updatedSettings);
        updateSettings(updatedSettings);
    }

    /**
     * Resolves updated settings by trimming string values and checking for changes.
     * It only includes settings that have changed from the current settings.
     * @param newSettings
     * @returns {object} - An object containing only the updated and trimmed settings
     */
    const resolveUpdatedSettings = (newSettings) => {
        const updatedSettings = {};
        // Trim string values in the form data
        const trimmedSettings = Object.fromEntries(
            Object.entries(newSettings).map(([key, value]) => (
                [ key, sanitizeValue(key, value) ]
            ))
        );
        // Only update settings that have changed
        for (const key in trimmedSettings) {
            if (settings[key] !== trimmedSettings[key] && !(settings[key] === undefined && trimmedSettings[key] === '')) {
                updatedSettings[key] = trimmedSettings[key];
            }
        }
        return updatedSettings;
    }

    /**
     * Sanitizes the value based on the key.
     * For 'phone', it removes spaces; for other strings, it trims whitespace.
     * @param key
     * @param value
     * @returns {*}
     */
    const sanitizeValue = (key, value) => {
        if (typeof value !== 'string') {
            return value;
        }
        if (key === 'phone') {
            value = value.replaceAll(' ', '');
        } else {
            value = value.trim();
        }
        return value;
    }

    return {
        getUserAccountCompanySettings,
        updateUserAccountCompanySettings,
        getUserAccountSettings,
        updateUserAccountSettings,
        settings
    }
}

export default useUserAccountSettings;
